import React, { JSX, useContext, useMemo } from "react";
import { BenchmarkingFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters/BenchmarkingFiltersContext";
import { NetworkComparisonFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters";
import { BenchmarkingDashboardDetailsTypeEnum } from "@app/overviews/network-benchmarking-dashboard/dashboard-details";
import { Accordion } from "@app/shared/Accordion";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import { BenchmarkingContext } from "@app/overviews/network-benchmarking-dashboard/BenchmarkingContext";
import { BenchmarkingSpecialtyGroup } from "@app/overviews/network-benchmarking-dashboard/filters/network-comparison-filters/shared/BenchmarkingSpecialtyGroup";
import { BenchmarkingCounty } from "@app/overviews/network-benchmarking-dashboard/filters/shared/BenchmarkingCounty";
import { BenchmarkingLineOfBusinessEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared/BenchmarkingLineOfBusinessEnum";
import { QHPPrimaryFiltersPayload } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/shared/QHPPrimaryFiltersPayload";
import { MedicarePrimaryFiltersPayload } from "@app/overviews/network-benchmarking-dashboard/medicare/filters/shared/MedicarePrimaryFiltersPayload";
import { MembershipCountTypeEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared/MembershipCountTypeEnum";
import { MedicaidPrimaryFiltersPayload } from "@app/overviews/network-benchmarking-dashboard/medicaid/filters/shared/MedicaidPrimaryFiltersPayload";
import { BenchmarkingNetworkType } from "@app/overviews/network-benchmarking-dashboard/filters/shared/benchmarkingNetworkType";

const BenchmarkingFiltersOverview = (): JSX.Element => {
  const networkComparisonFiltersContext = useContext(NetworkComparisonFiltersContext);
  const benchmarkingFiltersContext = useContext(BenchmarkingFiltersContext);
  const benchmarkingContext = useContext(BenchmarkingContext);

  const accordionTitle: string | JSX.Element = useMemo(() => {
    let totalFilters = 0;

    if (!!benchmarkingFiltersContext.displayedPrimaryFilters && !!networkComparisonFiltersContext.displayedNetworkComparisonFilters) {
      const getLobSpecificFilterCount = (): number => {
        switch (benchmarkingFiltersContext.selectedLineOfBusiness) {
          case BenchmarkingLineOfBusinessEnum.Marketplace:
            return (!!(benchmarkingFiltersContext.displayedPrimaryFilters as QHPPrimaryFiltersPayload).networkType ? 1 : 0);
          case BenchmarkingLineOfBusinessEnum.Medicare:
            return (!!(benchmarkingFiltersContext.displayedPrimaryFilters as MedicarePrimaryFiltersPayload).snpFilter ? 1 : 0) +
              (!!(benchmarkingFiltersContext.displayedPrimaryFilters as MedicarePrimaryFiltersPayload).membershipCount ? 1 : 0);
          case BenchmarkingLineOfBusinessEnum.Medicaid:
            return (!!(benchmarkingFiltersContext.displayedPrimaryFilters as MedicaidPrimaryFiltersPayload).programKey ? 1 : 0);
          default:
            return 0;
        }
      }

      totalFilters = (!!benchmarkingFiltersContext.displayedPrimaryFilters.lineOfBusinessId ? 1 : 0) +
        1 + // this accounts for the selected state, as we can only select one state and it is required
        benchmarkingFiltersContext.displayedPrimaryFilters.countyIds.length +
        (benchmarkingFiltersContext.displayedPrimaryFilters.starRatings?.length ?? 0) +
        (benchmarkingFiltersContext.displayedPrimaryFilters.planTypes?.length ?? 0) +
        getLobSpecificFilterCount() +
        (!!networkComparisonFiltersContext.displayedNetworkComparisonFilters.primaryNetwork ? 1 : 0) +
        networkComparisonFiltersContext.displayedNetworkComparisonFilters.comparisonNetworks.length +
        networkComparisonFiltersContext.displayedNetworkComparisonFilters.specialtyGroupIds.length +
        (!!networkComparisonFiltersContext.displayedNetworkComparisonFilters.pcaLineOfBusiness ? 1 : 0) +
        networkComparisonFiltersContext.displayedNetworkComparisonFilters.volumeIndicators.length +
        networkComparisonFiltersContext.displayedNetworkComparisonFilters.effectivenessRanks.length +
        networkComparisonFiltersContext.displayedNetworkComparisonFilters.efficiencyRanks.length;
    }

    return `Filters: ${totalFilters} Applied`;
  }, [
    benchmarkingFiltersContext.displayedPrimaryFilters,
    networkComparisonFiltersContext.displayedNetworkComparisonFilters,
  ]);

  const displayedFilters = useMemo(() => {
    if (!!benchmarkingFiltersContext.displayedPrimaryFilters && !!networkComparisonFiltersContext.displayedNetworkComparisonFilters) {
      const lineOfBusiness: JSX.Element = (
        <span className="dashboard-filters-overview__filter">
          {BenchmarkingLineOfBusinessEnum[benchmarkingFiltersContext.displayedPrimaryFilters.lineOfBusinessId]}
        </span>
      );

      const getStateName = (): string => {
        const countyId = benchmarkingFiltersContext.displayedPrimaryFilters.countyIds[0];
        const stateOption = benchmarkingFiltersContext.states.find(x => x.counties.map(y => y.countyId).includes(countyId));
        return stateOption?.stateName;
      }

      const state: JSX.Element = (
        <span className="dashboard-filters-overview__filter">
          {getStateName()}
        </span>
      );

      const getCountyName = (countyId: number): string => {
        const county: BenchmarkingCounty = benchmarkingFiltersContext.rawCounties.find(x => x.countyId === countyId);

        if (!!county) {
          return county.countyName;
        }
      }

      const getContractEntityName = (professionalContractEntityId: number): string => {
        return benchmarkingContext.affiliationsData.find(x => x.professionalContractEntityId === professionalContractEntityId)?.contractEntityName;
      }

      const counties: JSX.Element[] = benchmarkingFiltersContext.displayedPrimaryFilters.countyIds.map((county) => (
        <span key={county} className="dashboard-filters-overview__filter">
          {getCountyName(county)}
        </span>
      ));

      const cmsStarRatings: JSX.Element[] = (benchmarkingFiltersContext.displayedPrimaryFilters.starRatings ?? []).map((starRating) => (
        <span key={starRating} className="dashboard-filters-overview__filter">
          {starRating}
        </span>
      ));

      const planTypes: JSX.Element[] = (benchmarkingFiltersContext.displayedPrimaryFilters.planTypes ?? []).map((planType) => (
        <span key={planType} className="dashboard-filters-overview__filter">
          {planType}
        </span>
      ));

      const snpPlan: JSX.Element = benchmarkingFiltersContext.selectedLineOfBusiness === BenchmarkingLineOfBusinessEnum.Medicare && (
        <span className="dashboard-filters-overview__filter">
          {(benchmarkingFiltersContext.displayedPrimaryFilters as MedicarePrimaryFiltersPayload).snpFilter}
        </span>
      );

      const membership: JSX.Element = benchmarkingFiltersContext.selectedLineOfBusiness === BenchmarkingLineOfBusinessEnum.Medicare && (
        <span className="dashboard-filters-overview__filter">
          {(benchmarkingFiltersContext.displayedPrimaryFilters as MedicarePrimaryFiltersPayload).membershipCount}
          {(benchmarkingFiltersContext.displayedPrimaryFilters as MedicarePrimaryFiltersPayload).membershipCount !== MembershipCountTypeEnum.All ? "+" : ""}
        </span>
      );

      const networkType: JSX.Element = benchmarkingFiltersContext.selectedLineOfBusiness === BenchmarkingLineOfBusinessEnum.Marketplace && (
        <span className="dashboard-filters-overview__filter">
          {(benchmarkingFiltersContext.displayedPrimaryFilters as QHPPrimaryFiltersPayload).networkType}
        </span>
      );

      const program: JSX.Element = benchmarkingFiltersContext.selectedLineOfBusiness === BenchmarkingLineOfBusinessEnum.Medicaid && (
        <span className="dashboard-filters-overview__filter">
          {(benchmarkingFiltersContext.displayedPrimaryFilters as MedicaidPrimaryFiltersPayload).programKey}
        </span>
      );

      const getNetworkDisplayName = (n: BenchmarkingNetworkType): string => {
        switch (benchmarkingFiltersContext.selectedLineOfBusiness) {
          case BenchmarkingLineOfBusinessEnum.Medicaid:
            return n?.parentOrganization;
          default:
            return n?.contractId;
        }
      }

      const primaryNetwork: JSX.Element = (
        <span className="dashboard-filters-overview__filter">
          {getNetworkDisplayName(networkComparisonFiltersContext.displayedNetworkComparisonFilters.primaryNetwork)}
        </span>
      );

      const comparisonNetworks: JSX.Element[] = networkComparisonFiltersContext.displayedNetworkComparisonFilters.comparisonNetworks.map((network, index) => (
        <span key={index} className="dashboard-filters-overview__filter">
          {getNetworkDisplayName(network)}
        </span>
      ));

      const getSpecialtyName = (specialtyGroupId: number): string => {
        const specialty: BenchmarkingSpecialtyGroup = benchmarkingFiltersContext.rawSpecialties.find(x => x.specialtyGroupId === specialtyGroupId);
        return specialty?.specialtyGroupName;
      }

      const specialties: JSX.Element[] = networkComparisonFiltersContext.displayedNetworkComparisonFilters.specialtyGroupIds.map((specialtyGroupId) => (
        <span key={specialtyGroupId} className="dashboard-filters-overview__filter">
          {getSpecialtyName(specialtyGroupId)}
        </span>
      ));

      const pcaLineOfBusiness: JSX.Element = (
        <span className="dashboard-filters-overview__filter">
          {networkComparisonFiltersContext.displayedNetworkComparisonFilters.pcaLineOfBusiness}
        </span>
      );

      const volumeIndicators: JSX.Element[] = networkComparisonFiltersContext.displayedNetworkComparisonFilters.volumeIndicators.map((volumeIndicator) => (
        <span key={volumeIndicator} className="dashboard-filters-overview__filter">
          {volumeIndicator}
        </span>
      ));

      const effectiveness: JSX.Element[] = networkComparisonFiltersContext.displayedNetworkComparisonFilters.effectivenessRanks.map((effectivenessRank) => (
        <span key={effectivenessRank} className="dashboard-filters-overview__filter">
          {effectivenessRank}
        </span>
      ));

      const efficiency: JSX.Element[] = networkComparisonFiltersContext.displayedNetworkComparisonFilters.efficiencyRanks.map((efficiencyRank) => (
        <span key={efficiencyRank} className="dashboard-filters-overview__filter">
          {efficiencyRank}
        </span>
      ));

      const contractEntity: JSX.Element = (
        <span className="dashboard-filters-overview__filter">
          {getContractEntityName(networkComparisonFiltersContext.displayedNetworkComparisonFilters.professionalContractEntityId)}
        </span>
      );


      return {
        cmsStarRatings,
        comparisonNetworks,
        contractEntity,
        counties,
        effectiveness,
        efficiency,
        lineOfBusiness,
        membership,
        networkType,
        pcaLineOfBusiness,
        planTypes,
        primaryNetwork,
        program,
        snpPlan,
        specialties,
        state,
        volumeIndicators,
      };
    }
  }, [
    benchmarkingFiltersContext.displayedPrimaryFilters,
    networkComparisonFiltersContext.displayedNetworkComparisonFilters,
  ]);

  return (
    <div className="row">
      <div className="col-12">
        <AccordionContainer>
          <Accordion
            testId="benchmarking-dashboard__filters-applied--accordion-panel"
            title={accordionTitle}
          >
            <div className="d-flex">
              <span className="dashboard-filters-overview__label">Line Of Business:</span>
              <span>{displayedFilters?.lineOfBusiness}</span>
            </div>
            <div className="d-flex">
              <span className="dashboard-filters-overview__label">State:</span>
              <span>{displayedFilters?.state}</span>
            </div>
            <div className="d-flex">
              <span className="dashboard-filters-overview__label">County:</span>
              <span>{displayedFilters?.counties}</span>
            </div>
            {benchmarkingFiltersContext.selectedLineOfBusiness !== BenchmarkingLineOfBusinessEnum.Medicaid && (
              <React.Fragment>
                <div className="d-flex">
                  <span className="dashboard-filters-overview__label">CMS Star Rating:</span>
                  <span>{displayedFilters?.cmsStarRatings}</span>
                </div>
                <div className="d-flex">
                  <span className="dashboard-filters-overview__label">Plan Type:</span>
                  <span>{displayedFilters?.planTypes}</span>
                </div>
              </React.Fragment>
            )}
            {benchmarkingFiltersContext.selectedLineOfBusiness === BenchmarkingLineOfBusinessEnum.Medicare && (
              <React.Fragment>
                <div className="d-flex">
                  <span className="dashboard-filters-overview__label">SNP Plan:</span>
                  <span>{displayedFilters?.snpPlan}</span>
                </div>
                <div className="d-flex">
                  <span className="dashboard-filters-overview__label">Membership:</span>
                  <span>{displayedFilters?.membership}</span>
                </div>
              </React.Fragment>
            )}
            {benchmarkingFiltersContext.selectedLineOfBusiness === BenchmarkingLineOfBusinessEnum.Marketplace && (
              <div className="d-flex">
                <span className="dashboard-filters-overview__label">Network Type:</span>
                <span>{displayedFilters?.networkType}</span>
              </div>
            )}
            {benchmarkingFiltersContext.selectedLineOfBusiness === BenchmarkingLineOfBusinessEnum.Medicaid && (
              <div className="d-flex">
                <span className="dashboard-filters-overview__label">Program:</span>
                <span>{displayedFilters?.program}</span>
              </div>
            )}
            <div className="d-flex">
              <span className="dashboard-filters-overview__label">Primary Network:</span>
              <span>{displayedFilters?.primaryNetwork}</span>
            </div>
            <div className="d-flex">
              <span className="dashboard-filters-overview__label">Comparison Network(s):</span>
              <span>{displayedFilters?.comparisonNetworks}</span>
            </div>
            <div className="d-flex">
              <span className="dashboard-filters-overview__label">Specialty:</span>
              <span>{displayedFilters?.specialties}</span>
            </div>
            <div className="d-flex">
              <span className="dashboard-filters-overview__label">PCA Line of Business:</span>
              <span>{displayedFilters?.pcaLineOfBusiness}</span>
            </div>
            <div className="d-flex">
              <span className="dashboard-filters-overview__label">Volume Indicator:</span>
              <span>{displayedFilters?.volumeIndicators}</span>
            </div>
            <div className="d-flex">
              <span className="dashboard-filters-overview__label">Effectiveness:</span>
              <span>{displayedFilters?.effectiveness}</span>
            </div>
            <div className="d-flex">
              <span className="dashboard-filters-overview__label">Efficiency:</span>
              <span>{displayedFilters?.efficiency}</span>
            </div>

            {networkComparisonFiltersContext.displayedNetworkComparisonFilters.drillinOption === BenchmarkingDashboardDetailsTypeEnum.Affiliations && (
              <div className="d-flex">
                <span className="dashboard-filters-overview__label">Contract Entitiy:</span>
                <span>{displayedFilters.contractEntity}</span>
              </div>
            )}

          </Accordion>
        </AccordionContainer>
      </div>
    </div>
  );
}

export default BenchmarkingFiltersOverview;